import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import { isEqual, omit, orderBy } from 'lodash'
import { API, graphqlOperation } from 'aws-amplify'
import { Auth } from '@aws-amplify/auth'
import * as queries from '../../graphql/queries'
import * as mutations from '../../graphql/mutations'
import { makeErrorToast, msg, makeSuccessToast } from '../../utils/makeToast'
import { fetchProposals, setCurrentProposal, setCurrentProposalInvoices } from './proposals'
import i18next from 'i18next'
import dayjs from 'dayjs'

import {
  fetchPurchasesByTenantIdUtil,
  fetchQuotesByTenantIdUtil,
  fetchSuppliersByTenantIdUtil,
  fetchProjectsByTenantIdUtil,
} from '@src/utils/fetchUtils'

import config from '@src/aws-exports'

const { aws_user_files_s3_bucket_region: region, aws_user_files_s3_bucket: bucket } = config

export const fetchPurchases = createAsyncThunk(
  'purchases/fetchPurchases',
  async (arg, { getState, rejectWithValue }) => {
    const { auth, projects, suppliers, purchases, settings } = getState()
    try {
      const sort = settings.sort
      // const purchasesDataPromise = API.graphql(
      //   graphqlOperation(queries.purchasesByTenantId, {
      //     tenantId: auth.userInfo.tenantId,
      //     filter: { activeFlag: { eq: true } },
      //   })
      // )
      const purchasesDataPromise = fetchPurchasesByTenantIdUtil(auth.userInfo.tenantId)
      const suppliersDataPromise = fetchSuppliersByTenantIdUtil(auth.userInfo.tenantId)

      const quoteDataPromise = fetchQuotesByTenantIdUtil(auth.userInfo.tenantId)

      const projectDataPromise = fetchProjectsByTenantIdUtil(auth.userInfo.tenantId)

      const [quoteData, projectData, purchaseData, suppliersData] = await Promise.all([
        quoteDataPromise,
        projectDataPromise,
        purchasesDataPromise,
        suppliersDataPromise,
      ])
      purchaseData.map(i => {
        const currentSupplierData = suppliersData.filter(sd => sd.id === i.supplierId)
        const currentSupplierName =
          currentSupplierData.length > 0 ? currentSupplierData[0].name : i.supplierName || 'Deleted'
        i.supplierName = currentSupplierName

        if (i.purchaseType !== 'overhead') {
          const currentQuoteData = quoteData.filter(qd => qd.id === i.quoteId)
          const currentQuoteName =
            currentQuoteData.length > 0 ? currentQuoteData[0].name : i.quoteName || 'Deleted'
          const currentProjectID = currentQuoteData.length
            ? currentQuoteData[0].parentProjectId
            : null
          const currentProjectData = currentProjectID
            ? projectData.filter(pd => pd.id === currentProjectID)
            : null
          const currentProjectName = currentProjectData
            ? currentProjectData[0].name
            : i.projectName || 'Deleted'
          i.quoteName = currentQuoteName
          i.projectName = currentProjectName
        } else {
          i.recapCostlineItem = ''
          i.quoteName = ''
          i.projectName = 'Overhead'
        }
        return i
      })

      const sortedItems = orderBy(purchaseData, ['invoiceId'], ['asc'])

      return { sortedItems, quoteData, projectData }
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const createPurchases = createAsyncThunk(
  'purchases/createPurchases',
  async ({ purchaseData }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth, quotes, settings } = getState()
      // !Check if quoteId from purchaseData is same as the quoteId from redux
      // If saved the projectId is the parentProjectId from Redux as well as quoteName,projectName,
      // Else make an api call and get the quote details
      const {
        data: { getProjects: items },
      } = await API.graphql(
        graphqlOperation(queries.getProjects, {
          id: quotes?.currentQuote?.parentProjectId,
        })
      )
      const {
        data: { getRecapInfo: recapInfo },
      } = await API.graphql(
        graphqlOperation(queries.getRecapInfo, {
          id: purchaseData.recapCostlineId,
        })
      )
      const {
        data: { getQuote: quoteInfo },
      } = await API.graphql(
        graphqlOperation(queries.getQuote, {
          id: purchaseData.quoteId,
        })
      )
      const {
        data: { getProjects: projectInfo },
      } = await API.graphql(
        graphqlOperation(queries.getProjects, {
          id: quoteInfo.parentProjectId,
        })
      )

      let newPurchase = {
        tenantId: auth.userInfo.tenantId,
        activeFlag: true,
        poAvailable: false,
        invoiceAvailable: false,
        paymentStatus: 'Draft',
        purchaseType: 'costline',
        quoteName: quoteInfo.name,
        projectName: projectInfo.name,
        ...purchaseData,
      }
      const createdPurchase = await API.graphql(
        graphqlOperation(mutations.createPurchase, { input: newPurchase })
      )

      // let updateRecapInfo = {
      //   id: recapInfo.id,
      //   _version: recapInfo._version,
      //   purchaseId: createdPurchase.data.createPurchase.id,
      //   paymentStatus: 'Draft',
      // }
      // await API.graphql(graphqlOperation(mutations.updateRecapInfo, { input: updateRecapInfo }))
      dispatch(fetchPurchases())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const createOverhead = createAsyncThunk(
  'purchases/createOverhead',
  async ({ invoiceDetails, invoiceDocuments }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth, quotes, settings } = getState()
      // !Check if quoteId from purchaseData is same as the quoteId from redux
      // If saved the projectId is the parentProjectId from Redux as well as quoteName,projectName,
      // Else make an api call and get the quote details

      let newPurchase = {
        tenantId: auth.userInfo.tenantId,
        activeFlag: true,
        poAvailable: false,
        invoiceAvailable: true,
        purchaseType: 'overhead',
        projectName: 'Overhead',
        invoiceDocuments,
        supplierName: invoiceDetails.supplierName,
        supplierId: invoiceDetails.suppliers,
        invoiceTotal: parseFloat(invoiceDetails.amount),
        taxAmount: parseFloat(invoiceDetails.tax),
        supplierCreditorAccount: invoiceDetails.supplierCreditorAccount,
        purchaseInvoiceDueDate: invoiceDetails.date,
        invoiceKey: invoiceDocuments[0].key,
        paymentStatus: 'Pending',
      }
      console.log('newPurchase: ', newPurchase)
      const createdPurchase = await API.graphql(
        graphqlOperation(mutations.createPurchase, { input: newPurchase })
      )

      // let updateRecapInfo = {
      //   id: recapInfo.id,
      //   _version: recapInfo._version,
      //   purchaseId: createdPurchase.data.createPurchase.id,
      //   paymentStatus: 'Draft',
      // }
      // await API.graphql(graphqlOperation(mutations.updateRecapInfo, { input: updateRecapInfo }))
      dispatch(fetchPurchases())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const updatePurchase = createAsyncThunk(
  'purchases/updatePurchase',
  async ({ purchaseData }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth, quotes, settings } = getState()
      // !Check if quoteId from purchaseData is same as the quoteId from redux
      // If saved the projectId is the parentProjectId from Redux as well as quoteName,projectName,
      // Else make an api call and get the quote details
      const {
        data: { getPurchase: items },
      } = await API.graphql(
        graphqlOperation(queries.getPurchase, {
          id: purchaseData.purchaseId,
        })
      )

      let updatePurchase = {}
      updatePurchase.id = items.id
      updatePurchase._version = items._version
      delete purchaseData.id
      for (const property in purchaseData) {
        if (!isEqual(purchaseData[property], items[property])) {
          updatePurchase[property] = purchaseData[property]
        }
      }
      delete updatePurchase.purchaseId
      if (Object.keys(updatePurchase).length > 2) {
        await API.graphql(graphqlOperation(mutations.updatePurchase, { input: updatePurchase }))
      }

      dispatch(fetchPurchases())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const updatePaymentStatus = createAsyncThunk(
  'purchases/updatePaymentStatus',
  async ({ date, currentPurchaseId }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getPurchase },
      } = await API.graphql(graphqlOperation(queries.getPurchase, { id: currentPurchaseId }))
      let updatedPurchase = {
        id: getPurchase.id,
        _version: getPurchase._version,
        paymentStatus: 'Paid',
        purchasePaidDate: dayjs(date).toISOString(),
      }
      await API.graphql(graphqlOperation(mutations.updatePurchase, { input: updatedPurchase }))
      if (getPurchase.purchaseType !== 'overhead') {
        const {
          data: { getRecapInfo },
        } = await API.graphql(
          graphqlOperation(queries.getRecapInfo, { id: getPurchase.recapCostlineId })
        )
        let updatedRecap = {
          id: getRecapInfo.id,
          _version: getRecapInfo._version,
          paymentStatus: 'Paid',
          // paymentStatus: currentPaymentStatus,
        }

        await API.graphql(graphqlOperation(mutations.updateRecapInfo, { input: updatedRecap }))
      }
      dispatch(fetchPurchases())
      makeSuccessToast(i18next.t('invoices:toasts.markPaid'))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const markAsUnpaid = createAsyncThunk(
  'purchases/markAsUnpaid',
  async ({ currentPurchaseId }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getPurchase },
      } = await API.graphql(graphqlOperation(queries.getPurchase, { id: currentPurchaseId }))
      let currentPaymentStatus = ''
      if (!getPurchase.invoiceAvailable) {
        currentPaymentStatus = 'Draft'
      } else {
        if (dayjs(getPurchase.purchaseInvoiceDueDate).diff(dayjs(), 'day') >= 0) {
          currentPaymentStatus = 'Pending'
        } else if (dayjs(getPurchase.purchaseInvoiceDueDate).diff(dayjs(), 'day') < 0) {
          currentPaymentStatus = 'Overdue'
        }
      }

      let updatedPurchase = {
        id: getPurchase.id,
        _version: getPurchase._version,
        // paymentStatus: 'Pending',
        paymentStatus: currentPaymentStatus,
      }
      await API.graphql(graphqlOperation(mutations.updatePurchase, { input: updatedPurchase }))
      if (getPurchase.purchaseType !== 'overhead') {
        const {
          data: { getRecapInfo },
        } = await API.graphql(
          graphqlOperation(queries.getRecapInfo, { id: getPurchase.recapCostlineId })
        )
        let updatedRecap = {
          id: getRecapInfo.id,
          _version: getRecapInfo._version,
          // paymentStatus: 'Pending',
          paymentStatus: currentPaymentStatus,
        }
        await API.graphql(graphqlOperation(mutations.updateRecapInfo, { input: updatedRecap }))
      }
      dispatch(fetchPurchases())
      makeSuccessToast(i18next.t('invoices:toasts.markUnpaid'))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const dailyUpdatePaymentStatus = createAsyncThunk(
  'purchases/dailyUpdatePaymentStatus',
  async ({ currentPurchase }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getPurchase },
      } = await API.graphql(graphqlOperation(queries.getPurchase, { id: currentPurchase.id }))

      let updatedPurchase = {
        id: getPurchase.id,
        _version: getPurchase._version,
        paymentStatus: 'Overdue',
      }
      await API.graphql(graphqlOperation(mutations.updatePurchase, { input: updatedPurchase }))
      if (getPurchase.purchaseType !== 'overhead') {
        const {
          data: { getRecapInfo },
        } = await API.graphql(
          graphqlOperation(queries.getRecapInfo, { id: getPurchase.recapCostlineId })
        )
        let updatedRecap = {
          id: getRecapInfo.id,
          _version: getRecapInfo._version,
          paymentStatus: 'Overdue',
        }
        await API.graphql(graphqlOperation(mutations.updateRecapInfo, { input: updatedRecap }))
      }
      dispatch(fetchPurchases())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const updateArchiveStatus = createAsyncThunk(
  'purchases/updateArchiveStatus',
  async ({ currentPurchaseId }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getPurchase },
      } = await API.graphql(graphqlOperation(queries.getPurchase, { id: currentPurchaseId }))
      let dueStatus = ''
      if (getPurchase.invoiceAvailable) {
        if (dayjs(getPurchase.purchaseInvoiceDueDate).diff(dayjs(), 'day') >= 0) {
          dueStatus = 'Pending'
        } else if (dayjs(getPurchase.purchaseInvoiceDueDate).diff(dayjs(), 'day') < 0) {
          dueStatus = 'Overdue'
        }
      } else {
        dueStatus = 'Draft'
      }
      let currentPaymentStatus = getPurchase.paymentStatus !== 'Archived' ? 'Archived' : dueStatus

      let updatedPurchase = {
        id: getPurchase.id,
        _version: getPurchase._version,
        // paymentStatus: 'Pending',
        paymentStatus: currentPaymentStatus,
        purchaseArchivedDate: dayjs().toISOString(),
      }
      await API.graphql(graphqlOperation(mutations.updatePurchase, { input: updatedPurchase }))
      if (getPurchase.purchaseType !== 'overhead') {
        const {
          data: { getRecapInfo },
        } = await API.graphql(
          graphqlOperation(queries.getRecapInfo, { id: getPurchase.recapCostlineId })
        )
        let updatedRecap = {
          id: getRecapInfo.id,
          _version: getRecapInfo._version,
          // paymentStatus: 'Pending',
          paymentStatus: currentPaymentStatus,
        }
        await API.graphql(graphqlOperation(mutations.updateRecapInfo, { input: updatedRecap }))
      }
      dispatch(fetchPurchases())
      makeSuccessToast(
        `Invoice succesfully  ${
          getPurchase.paymentStatus === 'Archived' ? 'unarchived' : 'archived'
        }`
      )
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const uploadInvoice = createAsyncThunk(
  'purchases/uploadInvoice',
  async ({ invoiceDetails, invoiceDocuments }, { dispatch, rejectWithValue }) => {
    try {
      for (let costlineId of invoiceDetails.costlines) {
        const {
          data: {
            purchasesByRecapCostlineId: { items },
          },
        } = await API.graphql(
          graphqlOperation(queries.purchasesByRecapCostlineId, { recapCostlineId: costlineId })
        )
        const {
          data: { getRecapInfo },
        } = await API.graphql(graphqlOperation(queries.getRecapInfo, { id: costlineId }))
        let updatePurchase = {
          id: items[0].id,
          invoiceDocuments,
          _version: items[0]._version,
          invoiceAvailable: true,
          invoiceTotal: invoiceDetails.amount,
          taxAmount: invoiceDetails.tax,
          supplierCreditorAccount: invoiceDetails.supplierCreditorAccount,
          purchaseInvoiceDueDate: invoiceDetails.date,
          invoiceKey: invoiceDocuments[0].key,
          paymentStatus: 'Pending',
        }
        let updateRecapInfo = {
          id: getRecapInfo.id,
          _version: getRecapInfo._version,
          invoiceAvailable: true,
          purchaseTotal: invoiceDetails.amount,
          paymentStatus: 'Pending',
        }
        await API.graphql(graphqlOperation(mutations.updatePurchase, { input: updatePurchase }))
        await API.graphql(graphqlOperation(mutations.updateRecapInfo, { input: updateRecapInfo }))
      }
      dispatch(fetchPurchases())
      makeSuccessToast('Invoice succesfully uploaded')
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const editInvoice = createAsyncThunk(
  'purchases/editInvoice',
  async (
    { currentPurchaseId, invoiceDetails, invoiceDocuments },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const {
        data: { getPurchase },
      } = await API.graphql(graphqlOperation(queries.getPurchase, { id: currentPurchaseId }))
      let updatePurchase = {}

      if (invoiceDocuments) {
        updatePurchase = {
          id: getPurchase.id,
          _version: getPurchase._version,
          invoiceTotal: parseFloat(invoiceDetails.amount),
          taxAmount: parseFloat(invoiceDetails.tax),
          supplierCreditorAccount: invoiceDetails.supplierCreditorAccount,
          purchaseInvoiceDueDate: invoiceDetails.date,
          invoiceKey: invoiceDocuments[0].key,
          invoiceDocuments,
        }
      } else {
        updatePurchase = {
          id: getPurchase.id,
          _version: getPurchase._version,
          invoiceTotal: parseFloat(invoiceDetails.amount),
          taxAmount: parseFloat(invoiceDetails.tax),
          supplierCreditorAccount: invoiceDetails.supplierCreditorAccount,
          purchaseInvoiceDueDate: invoiceDetails.date,
        }
      }
      let updateRecapInfo = {}
      if (getPurchase.purchaseType !== 'overhead') {
        const {
          data: { getRecapInfo },
        } = await API.graphql(
          graphqlOperation(queries.getRecapInfo, { id: getPurchase.recapCostlineId })
        )
        updateRecapInfo = {
          id: getRecapInfo.id,
          _version: getRecapInfo._version,
          purchaseTotal: invoiceDetails.amount,
        }
        if (
          !isEqual(invoiceDetails.date, getPurchase.purchaseInvoiceDueDate) &&
          getPurchase.paymentStatus !== 'Paid' &&
          getPurchase.paymentStatus !== 'Archived'
        ) {
          if (dayjs(invoiceDetails.date).diff(dayjs(), 'day') >= 0) {
            updatePurchase.paymentStatus = 'Pending'
            updateRecapInfo.paymentStatus = 'Pending'
          } else if (dayjs(invoiceDetails.date).diff(dayjs(), 'day') < 0) {
            updatePurchase.paymentStatus = 'Overdue'
            updateRecapInfo.paymentStatus = 'Overdue'
          }
        }
      }
      await API.graphql(graphqlOperation(mutations.updatePurchase, { input: updatePurchase }))
      if (getPurchase.purchaseType !== 'overhead') {
        await API.graphql(graphqlOperation(mutations.updateRecapInfo, { input: updateRecapInfo }))
      }
      dispatch(fetchPurchases())
      makeSuccessToast('Succesfully Updated')
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const deleteInvoice = createAsyncThunk(
  'purchases/deleteInvoice',
  async ({ currentPurchaseId }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getPurchase },
      } = await API.graphql(graphqlOperation(queries.getPurchase, { id: currentPurchaseId }))
      const {
        data: { getSuppliers },
      } = await API.graphql(graphqlOperation(queries.getSuppliers, { id: getPurchase.supplierId }))
      let updatePurchase = {
        id: getPurchase.id,
        _version: getPurchase._version,
        invoiceTotal: null,
        taxAmount: null,
        supplierCreditorAccount: getSuppliers.supplierCreditorAccount,
        purchaseInvoiceDueDate: null,
        paymentStatus: 'Draft',
        invoiceDocuments: [],
        invoiceAvailable: false,
        invoiceKey: null,
      }
      await API.graphql(graphqlOperation(mutations.updatePurchase, { input: updatePurchase }))
      if (getPurchase.purchaseType !== 'overhead') {
        const {
          data: { getRecapInfo },
        } = await API.graphql(
          graphqlOperation(queries.getRecapInfo, { id: getPurchase.recapCostlineId })
        )
        let updateRecapInfo = {
          id: getRecapInfo.id,
          _version: getRecapInfo._version,
          paymentStatus: 'Draft',
          purchaseTotal: null,
          invoiceAvailable: false,
        }
        await API.graphql(graphqlOperation(mutations.updateRecapInfo, { input: updateRecapInfo }))
      }
      dispatch(fetchPurchases())
      makeSuccessToast('Deleted Invoice')
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const purchasesSlice = createSlice({
  name: 'purchases',
  initialState: {
    purchasesData: [],
    quoteData: [],
    projectData: [],
    currentPurchaseInfo: null,
    fetchPurchasesApiStatus: 'idle',
    createPurchaseApiStatus: 'idle',
    createOverheadApiStatus: 'idle',
    updatePurchaseApiStatus: 'idle',
    updatePaymentApiStatus: 'idle',
    updateArchiveApiStatus: 'idle',
    uploadInvoiceApiStatus: 'idle',
    editInvoiceApiStatus: 'idle',
    deletePurchaseInvoiceApiStatus: 'idle',
    addInvoiceModalOpenStatus: false,
    addEmailInvoiceModalOpenStatus: false,
    editInvoiceModalOpenStatus: false,
    deleteInvoiceDialogOpenStatus: false,
    purchasePaidConfirmationDialogOpenStatus: false,
    purchaseUnpaidConfirmationDialogOpenStatus: false,
    purchaseArchivedConfirmationDialogOpenStatus: false,
    purchaseDownloadConfirmationDialogOpenStatus: false,
    error: null,
  },
  reducers: {
    setPurchaseArchivedConfirmationDialogOpenStatus: (state, action) => {
      state.purchaseArchivedConfirmationDialogOpenStatus = action.payload.status
    },
    setPurchasePaidConfirmationDialogOpenStatus: (state, action) => {
      state.purchasePaidConfirmationDialogOpenStatus = action.payload.status
    },
    setPurchaseUnpaidConfirmationDialogOpenStatus: (state, action) => {
      state.purchaseUnpaidConfirmationDialogOpenStatus = action.payload.status
    },
    setPurchaseDownloadConfirmationDialogOpenStatus: (state, action) => {
      state.purchaseDownloadConfirmationDialogOpenStatus = action.payload.status
    },
    setFetchPurchaseApiStatus: (state, action) => {
      state.fetchPurchasesApiStatus = 'loading'
    },
    setAddInvoiceModalOpenStatus: (state, action) => {
      state.addInvoiceModalOpenStatus = action.payload.status
    },
    setAddEmailInvoiceModalOpenStatus: (state, action) => {
      state.addEmailInvoiceModalOpenStatus = action.payload.status
    },
    setEditInvoiceModalOpenStatus: (state, action) => {
      state.editInvoiceModalOpenStatus = action.payload.status
    },
    setCurrentPurchase: (state, action) => {
      state.currentPurchaseInfo = action.payload
    },
    setDeleteInvoiceDialogOpenStatus: (state, action) => {
      state.deleteInvoiceDialogOpenStatus = action.payload.status
    },
  },
  extraReducers: {
    [fetchPurchases.pending]: (state, action) => {
      state.fetchPurchasesApiStatus = 'loading'
    },
    [fetchPurchases.fulfilled]: (state, action) => {
      state.fetchPurchasesApiStatus = 'succeeded'
      state.purchasesData = action.payload.sortedItems
      state.quoteData = action.payload.quoteData
      state.projectData = action.payload.projectData
    },
    [fetchPurchases.rejected]: (state, action) => {
      state.fetchPurchasesApiStatus = 'failed'
      state.error = action.error.message
    },
    [createPurchases.pending]: (state, action) => {
      state.createPurchaseApiStatus = 'loading'
    },
    [createPurchases.fulfilled]: (state, action) => {
      state.createPurchaseApiStatus = 'succeeded'
    },
    [createPurchases.rejected]: (state, action) => {
      state.createPurchaseApiStatus = 'failed'
      state.error = action.error.message
    },
    [createOverhead.pending]: (state, action) => {
      state.createOverheadApiStatus = 'loading'
    },
    [createOverhead.fulfilled]: (state, action) => {
      state.createOverheadApiStatus = 'succeeded'
      state.addInvoiceModalOpenStatus = false
      state.addEmailInvoiceModalOpenStatus = false
    },
    [createOverhead.rejected]: (state, action) => {
      state.createOverheadApiStatus = 'failed'
      state.error = action.error.message
    },
    [updatePurchase.pending]: (state, action) => {
      state.updatePurchaseApiStatus = 'loading'
    },
    [updatePurchase.fulfilled]: (state, action) => {
      state.updatePurchaseApiStatus = 'succeeded'
    },
    [updatePurchase.rejected]: (state, action) => {
      state.updatePurchaseApiStatus = 'failed'
      state.error = action.error.message
    },
    [updatePaymentStatus.pending]: (state, action) => {
      state.updatePaymentApiStatus = 'loading'
    },
    [updatePaymentStatus.fulfilled]: (state, action) => {
      state.updatePaymentApiStatus = 'succeeded'
      state.purchasePaidConfirmationDialogOpenStatus = false
    },
    [updatePaymentStatus.rejected]: (state, action) => {
      state.updatePaymentApiStatus = 'failed'
      state.purchasePaidConfirmationDialogOpenStatus = false
      state.error = action.error.message
    },
    [markAsUnpaid.pending]: (state, action) => {
      state.updatePaymentApiStatus = 'loading'
    },
    [markAsUnpaid.fulfilled]: (state, action) => {
      state.updatePaymentApiStatus = 'succeeded'
      state.purchaseUnpaidConfirmationDialogOpenStatus = false
    },
    [markAsUnpaid.rejected]: (state, action) => {
      state.updatePaymentApiStatus = 'failed'
      state.purchaseUnpaidConfirmationDialogOpenStatus = false
      state.error = action.error.message
    },
    [updateArchiveStatus.pending]: (state, action) => {
      state.updateArchiveApiStatus = 'loading'
    },
    [updateArchiveStatus.fulfilled]: (state, action) => {
      state.updateArchiveApiStatus = 'succeeded'
      state.purchaseArchivedConfirmationDialogOpenStatus = false
    },
    [updateArchiveStatus.rejected]: (state, action) => {
      state.updateArchiveApiStatus = 'failed'
      state.purchaseArchivedConfirmationDialogOpenStatus = false
      state.error = action.error.message
    },
    [uploadInvoice.pending]: (state, action) => {
      state.uploadInvoiceApiStatus = 'loading'
    },
    [uploadInvoice.fulfilled]: (state, action) => {
      state.uploadInvoiceApiStatus = 'succeeded'
      state.addInvoiceModalOpenStatus = false
      state.addEmailInvoiceModalOpenStatus = false
    },
    [uploadInvoice.rejected]: (state, action) => {
      state.uploadInvoiceApiStatus = 'failed'
      state.error = action.error.message
    },
    [editInvoice.pending]: (state, action) => {
      state.editInvoiceApiStatus = 'loading'
    },
    [editInvoice.fulfilled]: (state, action) => {
      state.editInvoiceApiStatus = 'succeeded'
      state.editInvoiceModalOpenStatus = false
    },
    [editInvoice.rejected]: (state, action) => {
      state.editInvoiceApiStatus = 'failed'
      state.error = action.error.message
    },
    [deleteInvoice.pending]: (state, action) => {
      state.deletePurchaseInvoiceApiStatus = 'loading'
    },
    [deleteInvoice.fulfilled]: (state, action) => {
      state.deletePurchaseInvoiceApiStatus = 'succeeded'
    },
    [deleteInvoice.rejected]: (state, action) => {
      state.deletePurchaseInvoiceApiStatus = 'failed'
      state.error = action.error.message
    },
  },
})

export const {
  setPurchaseArchivedConfirmationDialogOpenStatus,
  setPurchasePaidConfirmationDialogOpenStatus,
  setFetchPurchasesApiStatus,
  setPurchaseUnpaidConfirmationDialogOpenStatus,
  setPurchaseDownloadConfirmationDialogOpenStatus,
  setAddInvoiceModalOpenStatus,
  setAddEmailInvoiceModalOpenStatus,
  setEditInvoiceModalOpenStatus,
  setCurrentPurchase,
  setDeleteInvoiceDialogOpenStatus,
} = purchasesSlice.actions

export default purchasesSlice.reducer
